// Yard management and selection share the same yard list. The row's Select
// button changes scope; administrator actions sit beside it, as in the catalogue.

import { fetchYards, createYard, updateYard, deleteYard, canManageYards } from '../yards.api.js';
import { getSelectedYardId, setSelectedYard } from '../../shared/tyre-scope.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';
import { icon } from '../../shared/tyre-icons.js';
import { esc } from '../../shared/tyre-format.js';

function yardSubtitle(yard) {
  const bits = [];
  if (yard.customerName) bits.push(yard.customerName);
  if (yard.IMEI) bits.push(`IMEI ${yard.IMEI}`);
  return bits.join(' \u00b7 ');
}

export function renderYards(viewEl) {
  const canManage = canManageYards();
  const tpl = document.createElement('template');
  tpl.innerHTML = `
    <div class="tm-yards">
      <section class="tm-yard-create">
        <div class="tm-catalogue-editor-head">
          <div>
            <h2 class="tm-catalogue-editor-title" data-editor-title>Add a yard</h2>
            <p class="tm-catalogue-editor-sub" data-editor-sub>Yards belong to their customer. Select one below to view its tyres.</p>
          </div>
          <span class="tm-catalogue-editor-ic">${icon('yards')}</span>
        </div>
        <div class="tm-yard-create-row">
          <label class="tm-catalogue-field">
            <span>Yard name <span class="tm-req">*</span></span>
            <input type="text" class="tm-yard-input" data-name autocomplete="off" maxlength="120" />
          </label>
          <label class="tm-catalogue-field">
            <span>IMEI (optional)</span>
            <input type="text" class="tm-yard-input" data-imei autocomplete="off" inputmode="numeric" />
          </label>
        </div>
        <div class="tm-yard-msg" data-msg hidden role="status" aria-live="polite"></div>
        <div class="tm-catalogue-editor-actions">
          <button type="button" class="tm-btn-secondary" data-cancel-edit hidden>Cancel edit</button>
          <button type="button" class="tm-btn-primary" data-save>Add yard</button>
        </div>
      </section>

      <section class="tm-yard-list-card">
        <div class="tm-catalogue-list-head">
          <div>
            <h2>Yards</h2>
            <p>Select a yard to view its tyres, or choose All yards for the whole customer.</p>
          </div>
          <span class="tm-yard-count" data-count></span>
        </div>
        <div class="tm-yard-list" data-list aria-live="polite"></div>
      </section>
    </div>`;

  const root = tpl.content.firstElementChild;
  const listEl = root.querySelector('[data-list]');
  const nameInput = root.querySelector('[data-name]');
  const imeiInput = root.querySelector('[data-imei]');
  const saveBtn = root.querySelector('[data-save]');
  const cancelBtn = root.querySelector('[data-cancel-edit]');
  const titleEl = root.querySelector('[data-editor-title]');
  const subtitleEl = root.querySelector('[data-editor-sub]');
  const countEl = root.querySelector('[data-count]');
  const msgEl = root.querySelector('[data-msg]');

  let yards = [];
  let loading = true;
  let error = null;
  let editingId = null;
  let deletingId = null;
  let busy = false;
  let disposed = false;
  let loadVersion = 0;

  function showMsg(message, kind = '') {
    msgEl.textContent = message || '';
    msgEl.hidden = !message;
    msgEl.className = `tm-yard-msg${kind ? ` is-${kind}` : ''}`;
  }

  function setBusy(value) {
    busy = value;
    nameInput.disabled = value;
    imeiInput.disabled = value;
    saveBtn.disabled = value;
    cancelBtn.disabled = value;
    renderList();
  }

  function clearEditor({ keepMessage = false } = {}) {
    editingId = null;
    nameInput.value = '';
    imeiInput.value = '';
    titleEl.textContent = 'Add a yard';
    subtitleEl.textContent = 'Yards belong to their customer. Select one below to view its tyres.';
    saveBtn.textContent = 'Add yard';
    cancelBtn.hidden = true;
    if (!keepMessage) showMsg('');
    renderList();
  }

  function startEdit(yard) {
    if (!canManageYards() || busy) return;
    editingId = yard.Id;
    deletingId = null;
    nameInput.value = yard.name || '';
    imeiInput.value = yard.IMEI || '';
    titleEl.textContent = 'Edit yard';
    subtitleEl.textContent = yard.customerName
      ? `Editing a yard for ${yard.customerName}.`
      : 'Edit this yard’s name and IMEI.';
    saveBtn.textContent = 'Save changes';
    cancelBtn.hidden = false;
    showMsg('');
    renderList();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    nameInput.focus();
    nameInput.select();
  }

  function rowMarkup(yard) {
    const id = yard?.Id ?? '';
    const active = id === (getSelectedYardId() || '');
    const isEditing = !!id && id === editingId;
    const isDeleting = !!id && id === deletingId;
    const name = yard?.name || 'All yards';
    const subtitle = yard ? yardSubtitle(yard) : 'Show the whole customer';
    const disabled = busy ? ' disabled' : '';

    return `
      <div class="tm-yard-item${active ? ' is-active' : ''}${isEditing ? ' is-editing' : ''}" data-yard-entry="${esc(id)}">
        <button type="button" class="tm-yard-row" data-yard-id="${esc(id)}" aria-pressed="${active ? 'true' : 'false'}" aria-label="Select ${esc(name)}"${disabled}>
          <span class="tm-yard-ic">${icon(yard ? 'yards' : 'tyre')}</span>
          <span class="tm-yard-main">
            <span class="tm-yard-name">${esc(name)}</span>
            <span class="tm-yard-sub">${esc(subtitle)}</span>
          </span>
          <span class="tm-yard-check" aria-hidden="true">${active ? icon('check') : ''}</span>
        </button>
        ${yard && canManage ? isDeleting ? `
          <div class="tm-yard-delete-confirm">
            <span>Delete “${esc(name)}”?</span>
            <button type="button" class="tm-btn-link" data-cancel-delete${disabled}>Cancel</button>
            <button type="button" class="tm-btn-danger-small" data-confirm-delete${disabled}>Delete</button>
          </div>` : `
          <div class="tm-yard-row-actions">
            <button type="button" class="tm-btn-link" data-edit-yard${disabled}>Edit</button>
            <button type="button" class="tm-btn-link tm-btn-link-danger" data-delete-yard${disabled}>Delete</button>
          </div>` : ''}
      </div>`;
  }

  function renderList() {
    if (loading) {
      countEl.textContent = '';
      listEl.innerHTML = '<div class="tm-yard-state">Loading yards…</div>';
      return;
    }
    if (error) {
      countEl.textContent = '';
      listEl.innerHTML = `<div class="tm-yard-state is-error">
        ${esc(error)} <button type="button" class="tm-btn-link" data-retry>Retry</button>
      </div>`;
      return;
    }

    countEl.textContent = `${yards.length} yard${yards.length === 1 ? '' : 's'}`;
    listEl.innerHTML = rowMarkup(null) + (yards.length
      ? yards.map(rowMarkup).join('')
      : '<div class="tm-yard-state">No yards yet. Add the first one above.</div>');
  }

  async function loadList() {
    const version = ++loadVersion;
    loading = true;
    error = null;
    renderList();

    const res = await fetchYards();
    if (disposed || version !== loadVersion) return;
    loading = false;
    if (!res.success) {
      error = res.message || 'Could not load yards.';
      renderList();
      return;
    }

    yards = Array.isArray(res.data?.results) ? res.data.results : [];
    const selectedId = getSelectedYardId();
    if (selectedId) {
      setSelectedYard(yards.find((yard) => yard.Id === selectedId) || null);
    }
    if (editingId && !yards.some((yard) => yard.Id === editingId)) clearEditor({ keepMessage: true });
    if (deletingId && !yards.some((yard) => yard.Id === deletingId)) deletingId = null;
    renderList();
  }

  async function saveYard() {
    if (busy) return;
    const name = nameInput.value.trim();
    const IMEI = imeiInput.value.trim();
    if (!name) {
      showMsg('Enter a yard name.', 'error');
      nameInput.focus();
      return;
    }

    const yard = editingId ? yards.find((item) => item.Id === editingId) : null;
    if (editingId && (!canManageYards() || !yard)) {
      showMsg('Administrator access and a valid yard are required to edit.', 'error');
      return;
    }

    setBusy(true);
    showMsg(yard ? 'Saving changes…' : 'Adding yard…');
    const res = yard ? await updateYard(yard, { name, IMEI }) : await createYard({ name, IMEI: IMEI || undefined });
    if (disposed) return;
    setBusy(false);

    if (!res.success) {
      showMsg(res.message || (yard ? 'Could not update the yard.' : 'Could not add the yard.'), 'error');
      return;
    }

    if (yard && getSelectedYardId() === yard.Id) {
      setSelectedYard({ ...yard, ...(res.data && typeof res.data === 'object' ? res.data : {}), name, IMEI });
    }
    clearEditor({ keepMessage: true });
    showMsg(yard ? `Yard “${name}” updated.` : `Yard “${name}” added.`, 'ok');
    nameInput.focus();
  }

  async function confirmDelete(yard) {
    if (busy || !canManageYards() || deletingId !== yard.Id) return;
    setBusy(true);
    const res = await deleteYard(yard);
    if (disposed) return;
    setBusy(false);

    if (!res.success) {
      showMsg(res.message || 'Could not delete the yard.', 'error');
      return;
    }

    if (getSelectedYardId() === yard.Id) setSelectedYard(null);
    if (editingId === yard.Id) clearEditor({ keepMessage: true });
    deletingId = null;
    yards = yards.filter((item) => item.Id !== yard.Id);
    renderList();
    showMsg(`Yard “${yard.name}” deleted.`, 'ok');
  }

  saveBtn.addEventListener('click', saveYard);
  cancelBtn.addEventListener('click', () => clearEditor());
  const onEnter = (event) => {
    if (event.key === 'Enter') { event.preventDefault(); saveYard(); }
  };
  nameInput.addEventListener('keydown', onEnter);
  imeiInput.addEventListener('keydown', onEnter);

  listEl.addEventListener('click', (event) => {
    if (event.target.closest('[data-retry]')) { loadList(); return; }
    const row = event.target.closest('[data-yard-entry]');
    if (!row || busy) return;
    const id = row.dataset.yardEntry;
    const yard = id ? yards.find((item) => item.Id === id) : null;

    if (event.target.closest('[data-yard-id]')) { setSelectedYard(yard || null); return; }
    if (!yard || !canManageYards()) return;
    if (event.target.closest('[data-edit-yard]')) { startEdit(yard); return; }
    if (event.target.closest('[data-delete-yard]')) { deletingId = id; renderList(); return; }
    if (event.target.closest('[data-cancel-delete]')) { deletingId = null; renderList(); return; }
    if (event.target.closest('[data-confirm-delete]')) confirmDelete(yard);
  });

  viewEl.replaceChildren(root);
  const offScope = onTyreEvent(TYRE_EVENTS.SCOPE_CHANGED, renderList);
  const offYards = onTyreEvent(TYRE_EVENTS.YARDS_CHANGED, loadList);
  loadList();
  nameInput.focus();

  return () => {
    disposed = true;
    offScope();
    offYards();
  };
}
